/**
 * CROEV Quiz V1 - Quiz Engine
 * Primal Viking - Testosterone Quiz
 * 
 * Features:
 * - Multi-step navigation with state management
 * - Single and multi-select question handling
 * - Auto-advance for single-select questions
 * - Loading animations with timed transitions
 * - Dynamic content population based on answers
 * - URL parameter generation for redirect
 * - localStorage backup of answers
 */

(function() {
  'use strict';

  // Age mapping for calculations
  const AGE_MAP = {
    'under_30': 25,
    '30_39': 35,
    '40_49': 45,
    '50_59': 55,
    'over_60': 65
  };

  // Age order for finding younger age (for comparison step)
  const AGE_ORDER = ['under_30', '30_39', '40_49', '50_59', 'over_60'];

  // Problem area labels for display
  const PROBLEM_LABELS = {
    'beer_belly': 'Beer Belly',
    'man_boobs': 'Man Boobs',
    'low_t': 'Low Testosterone',
    'high_cortisol': 'High Cortisol',
    'low_energy': 'Low Energy/Libido',
    'low_muscle': 'Low Muscle Mass'
  };

  class CroevQuiz {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.redirectUrl = container.dataset.redirectUrl;
      
      // Get quiz data from JSON
      const dataEl = document.getElementById(`croev-quiz-data-${this.sectionId}`);
      this.config = dataEl ? JSON.parse(dataEl.textContent) : {};
      
      // State
      this.state = {
        currentStep: 0,
        answers: {},
        history: []
      };
      
      // DOM elements
      this.steps = Array.from(container.querySelectorAll('.croev-quiz__step'));
      this.progressBar = container.querySelector('.croev-quiz__progress-bar');
      this.progressContainer = container.querySelector('.croev-quiz__progress');
      
      // Timers
      this.autoAdvanceTimer = null;
      this.checklistTimers = [];
      
      this.init();
    }

    init() {
      // Clear saved state on fresh page load (start quiz fresh each time)
      this.clearState();

      // Bind events
      this.bindEvents();

      // Show first step
      this.showStep(this.state.currentStep);

      // Update progress
      this.updateProgress();
    }

    bindEvents() {
      // Option selection
      this.container.addEventListener('click', (e) => {
        const option = e.target.closest('.croev-quiz__option');
        if (option) {
          this.handleOptionSelect(option);
        }
        
        const continueBtn = e.target.closest('[data-action="continue"]');
        if (continueBtn) {
          this.nextStep();
        }
        
        const backBtn = e.target.closest('[data-action="back"]');
        if (backBtn) {
          this.prevStep();
        }
        
        const claimBtn = e.target.closest('[data-action="claim-offer"]');
        if (claimBtn) {
          this.handleClaimOffer(claimBtn);
        }

        const skipBtn = e.target.closest('[data-action="skip"]');
        if (skipBtn) {
          this.handleSkip(skipBtn);
        }

        const unitBtn = e.target.closest('.croev-quiz__unit-btn');
        if (unitBtn) {
          this.handleUnitToggle(unitBtn);
        }
      });

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const option = e.target.closest('.croev-quiz__option');
          if (option) {
            e.preventDefault();
            this.handleOptionSelect(option);
          }
        }
      });

      // Input field handling
      this.container.addEventListener('input', (e) => {
        const input = e.target.closest('.croev-quiz__input');
        if (input) {
          this.handleInputChange(input);
        }
        const measInput = e.target.closest('.croev-quiz__measurement-input');
        if (measInput) {
          this.handleMeasurementChange(measInput);
        }
      });

      // Input field Enter key to continue
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const input = e.target.closest('.croev-quiz__input') || e.target.closest('.croev-quiz__measurement-input');
          if (input) {
            const step = input.closest('.croev-quiz__step');
            const continueBtn = step?.querySelector('[data-action="continue"]');
            if (continueBtn && !continueBtn.disabled) {
              this.nextStep();
            }
          }
        }
      });

    }

    handleInputChange(input) {
      const step = input.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const value = input.value.trim();
      const inputType = input.type;
      const min = input.min ? parseInt(input.min, 10) : null;
      const max = input.max ? parseInt(input.max, 10) : null;
      const required = input.hasAttribute('required');

      // Validate
      let isValid = true;
      const errorEl = step.querySelector('.croev-quiz__input-error');

      if (required && !value) {
        isValid = false;
      } else if (inputType === 'number' && value) {
        const numVal = parseInt(value, 10);
        if (isNaN(numVal)) {
          isValid = false;
        } else if (min !== null && numVal < min) {
          isValid = false;
        } else if (max !== null && numVal > max) {
          isValid = false;
        }
      } else if (inputType === 'email' && value) {
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }

      // Update UI
      input.classList.toggle('is-invalid', !isValid && value.length > 0);
      if (errorEl) {
        errorEl.hidden = isValid || value.length === 0;
      }

      // Store answer if valid
      if (isValid && value) {
        this.state.answers[questionId] = { value, label: value };
        this.saveState();
      } else {
        delete this.state.answers[questionId];
      }

      // Enable/disable continue button
      const continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn) {
        continueBtn.disabled = !isValid || !value;
      }
    }

    handleUnitToggle(btn) {
      const toggle = btn.closest('.croev-quiz__unit-toggle');
      const step = btn.closest('.croev-quiz__step');
      const unit = btn.dataset.unit;
      const wrapper = step.querySelector('.croev-quiz__measurement-wrapper');

      // Update toggle buttons
      toggle.querySelectorAll('.croev-quiz__unit-btn').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      // Update wrapper data attribute
      wrapper.dataset.activeUnit = unit;

      // Show/hide field groups
      wrapper.querySelectorAll('.croev-quiz__measurement-fields').forEach(group => {
        group.hidden = group.dataset.unitGroup !== unit;
      });

      // Clear inputs in the now-hidden group and re-validate the visible group
      wrapper.querySelectorAll('.croev-quiz__measurement-fields[hidden] .croev-quiz__measurement-input').forEach(input => {
        input.value = '';
      });

      // Re-validate the visible group
      const visibleInput = wrapper.querySelector('.croev-quiz__measurement-fields:not([hidden]) .croev-quiz__measurement-input');
      if (visibleInput) {
        this.handleMeasurementChange(visibleInput);
      } else {
        // Disable continue if no valid input
        const continueBtn = step.querySelector('[data-action="continue"]');
        if (continueBtn) continueBtn.disabled = true;
      }
    }

    handleMeasurementChange(input) {
      const step = input.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const measurementType = step.dataset.measurementType;
      const wrapper = step.querySelector('.croev-quiz__measurement-wrapper');
      const activeUnit = wrapper.dataset.activeUnit;
      const fields = wrapper.querySelector(`.croev-quiz__measurement-fields[data-unit-group="${activeUnit}"]`);
      const inputs = fields.querySelectorAll('.croev-quiz__measurement-input');
      const errorEl = step.querySelector('.croev-quiz__input-error');
      const continueBtn = step.querySelector('[data-action="continue"]');

      let isValid = true;
      let displayValue = '';
      let rawValue = '';

      if (measurementType === 'height' && activeUnit === 'imperial') {
        const feetInput = fields.querySelector('[data-measurement="feet"]');
        const inchesInput = fields.querySelector('[data-measurement="inches"]');
        const feet = feetInput.value.trim();
        const inches = inchesInput.value.trim();

        if (!feet && !inches) {
          isValid = false;
        } else {
          const feetNum = parseInt(feet, 10);
          const inchesNum = parseInt(inches || '0', 10);
          if (feet && (isNaN(feetNum) || feetNum < 3 || feetNum > 8)) {
            isValid = false;
          }
          if (inches && (isNaN(inchesNum) || inchesNum < 0 || inchesNum > 11)) {
            isValid = false;
          }
          if (!feet) {
            isValid = false;
          }
          if (isValid) {
            displayValue = `${feetNum}'${inchesNum}"`;
            rawValue = `${feetNum}ft ${inchesNum}in`;
          }
        }
      } else {
        // Single field (cm, lbs, kg)
        const singleInput = inputs[0];
        const val = singleInput.value.trim();
        const min = parseInt(singleInput.min, 10);
        const max = parseInt(singleInput.max, 10);

        if (!val) {
          isValid = false;
        } else {
          const numVal = parseInt(val, 10);
          if (isNaN(numVal) || numVal < min || numVal > max) {
            isValid = false;
          } else {
            const unit = singleInput.dataset.measurement;
            displayValue = `${numVal} ${unit}`;
            rawValue = `${numVal} ${unit}`;
          }
        }
      }

      // Update error state
      if (errorEl) {
        errorEl.hidden = isValid || !this.hasMeasurementInput(fields);
      }

      // Store answer
      if (isValid) {
        this.state.answers[questionId] = { value: rawValue, label: displayValue, unit: activeUnit };
        this.saveState();
      } else {
        delete this.state.answers[questionId];
      }

      // Enable/disable continue
      if (continueBtn) {
        continueBtn.disabled = !isValid;
      }
    }

    hasMeasurementInput(fields) {
      return Array.from(fields.querySelectorAll('.croev-quiz__measurement-input')).some(i => i.value.trim().length > 0);
    }

    handleOptionSelect(option) {
      const step = option.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const selectionType = step.dataset.selectionType;
      const autoAdvance = step.dataset.autoAdvance === 'true';
      const value = option.dataset.optionValue;
      const label = option.dataset.optionLabel;

      if (selectionType === 'single') {
        // Deselect all others
        step.querySelectorAll('.croev-quiz__option').forEach(opt => {
          opt.classList.remove('is-selected');
          opt.setAttribute('aria-pressed', 'false');
        });
        
        // Select this one
        option.classList.add('is-selected');
        option.setAttribute('aria-pressed', 'true');
        
        // Store answer
        this.state.answers[questionId] = { value, label };
        this.saveState();
        
        // Auto-advance if enabled
        if (autoAdvance) {
          setTimeout(() => this.nextStep(), 300);
        } else {
          // Enable continue button
          const continueBtn = step.querySelector('[data-action="continue"]');
          if (continueBtn) continueBtn.disabled = false;
        }
      } else {
        // Multi-select toggle
        option.classList.toggle('is-selected');
        const isSelected = option.classList.contains('is-selected');
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        
        // Gather all selected values
        const selectedOptions = Array.from(step.querySelectorAll('.croev-quiz__option.is-selected'));
        this.state.answers[questionId] = selectedOptions.map(opt => ({
          value: opt.dataset.optionValue,
          label: opt.dataset.optionLabel
        }));
        this.saveState();
        
        // Enable/disable continue button based on selection
        const continueBtn = step.querySelector('[data-action="continue"]');
        if (continueBtn) {
          continueBtn.disabled = selectedOptions.length === 0;
        }
      }
    }

    showStep(index) {
      // Clear any pending timers
      this.clearTimers();
      
      // Hide all steps
      this.steps.forEach((step, i) => {
        if (i === index) {
          step.classList.add('is-active');
          step.classList.remove('is-exiting');
        } else {
          step.classList.remove('is-active');
        }
      });

      const currentStep = this.steps[index];
      if (!currentStep) return;

      const stepType = currentStep.dataset.stepType;

      // Focus management - only auto-focus input fields, not options/buttons
      setTimeout(() => {
        const inputField = currentStep.querySelector('.croev-quiz__input');
        if (inputField) inputField.focus();
      }, 100);

      // Restore selections for question steps
      if (stepType === 'question_image' || stepType === 'question_text' || stepType === 'question_emoji') {
        this.restoreSelections(currentStep);
      }

      // Restore and handle input question steps
      if (stepType === 'question_input') {
        this.restoreInputValue(currentStep);
      }

      // Handle dynamic content for summary steps
      if (stepType === 'summary_profile') {
        this.populateSummaryProfile(currentStep);
      }

      if (stepType === 'summary_results') {
        this.populateSummaryResults(currentStep);
      }

      if (stepType === 'summary_comparison') {
        this.populateSummaryComparison(currentStep);
      }

      // Handle loading steps
      if (stepType === 'loading_step') {
        this.handleLoadingStep(currentStep);
      }

      // Handle info steps with dynamic content
      if (stepType === 'info_step') {
        this.populateInfoStep(currentStep);
      }

      // Handle offer step personalization + carousel
      if (stepType === 'offer_step') {
        this.populateOfferStep(currentStep);
        this.initOfferCarousel(currentStep);
      }

      // Update progress bar
      this.updateProgress();

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    restoreSelections(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      
      if (!answer) return;

      const isMulti = step.dataset.selectionType === 'multi';
      const values = isMulti ? answer.map(a => a.value) : [answer.value];

      step.querySelectorAll('.croev-quiz__option').forEach(option => {
        const optValue = option.dataset.optionValue;
        if (values.includes(optValue)) {
          option.classList.add('is-selected');
          option.setAttribute('aria-pressed', 'true');
        }
      });

      // Enable continue button if has selection
      const continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn && values.length > 0) {
        continueBtn.disabled = false;
      }
    }

    restoreInputValue(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      const measurementType = step.dataset.measurementType;

      // Measurement input restore
      if (measurementType && answer) {
        const unit = answer.unit || 'imperial';
        const wrapper = step.querySelector('.croev-quiz__measurement-wrapper');
        const toggle = step.querySelector('.croev-quiz__unit-toggle');

        // Restore unit toggle
        if (toggle) {
          toggle.querySelectorAll('.croev-quiz__unit-btn').forEach(btn => {
            btn.classList.toggle('is-active', btn.dataset.unit === unit);
          });
        }

        // Show correct field group
        if (wrapper) {
          wrapper.dataset.activeUnit = unit;
          wrapper.querySelectorAll('.croev-quiz__measurement-fields').forEach(group => {
            group.hidden = group.dataset.unitGroup !== unit;
          });

          // Parse and restore values
          const rawValue = answer.value || '';
          const fields = wrapper.querySelector(`.croev-quiz__measurement-fields[data-unit-group="${unit}"]`);

          if (measurementType === 'height' && unit === 'imperial') {
            const match = rawValue.match(/(\d+)ft\s*(\d+)in/);
            if (match) {
              const feetInput = fields.querySelector('[data-measurement="feet"]');
              const inchesInput = fields.querySelector('[data-measurement="inches"]');
              if (feetInput) feetInput.value = match[1];
              if (inchesInput) inchesInput.value = match[2];
            }
          } else {
            const match = rawValue.match(/(\d+)/);
            if (match && fields) {
              const input = fields.querySelector('.croev-quiz__measurement-input');
              if (input) input.value = match[1];
            }
          }

          // Re-validate
          const visibleInput = fields?.querySelector('.croev-quiz__measurement-input');
          if (visibleInput) this.handleMeasurementChange(visibleInput);
        }
        return;
      }

      // Standard input restore
      const input = step.querySelector('.croev-quiz__input');
      if (!answer || !input) return;

      input.value = answer.value || '';

      // Trigger validation to enable continue button if valid
      if (input.value) {
        this.handleInputChange(input);
      }
    }

    nextStep() {
      if (this.state.currentStep < this.steps.length - 1) {
        this.state.history.push(this.state.currentStep);
        this.state.currentStep++;

        // Auto-skip steps whose skip-if condition is met
        while (this.state.currentStep < this.steps.length - 1 && this.shouldSkipStep(this.steps[this.state.currentStep])) {
          this.state.currentStep++;
        }

        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    shouldSkipStep(step) {
      const skipIf = step.dataset.skipIf;
      if (!skipIf) return false;

      // Format: "question_id:value1,value2"
      const [questionId, valuesStr] = skipIf.split(':');
      if (!questionId || !valuesStr) return false;

      const skipValues = valuesStr.split(',').map(v => v.trim());
      const answer = this.state.answers[questionId];
      if (!answer) return false;

      const answerValue = Array.isArray(answer)
        ? answer.map(a => a.value)
        : [answer.value];

      return answerValue.some(v => skipValues.includes(v));
    }

    prevStep() {
      if (this.state.history.length > 0) {
        this.state.currentStep = this.state.history.pop();
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    updateProgress() {
      // Always re-query to ensure we have the element
      const bar = this.container.querySelector('.croev-quiz__progress-bar')
        || document.querySelector('.croev-quiz__progress-bar');
      const wrapper = this.container.querySelector('.croev-quiz__progress')
        || document.querySelector('.croev-quiz__progress');

      if (!bar) return;

      const progress = (this.state.currentStep / Math.max(this.steps.length - 1, 1)) * 100;
      bar.style.setProperty('width', `${progress}%`, 'important');

      if (wrapper) {
        wrapper.setAttribute('aria-valuenow', Math.round(progress));
      }
    }

    // --- Dynamic Content Population ---

    getPerceivedAge() {
      // Prefer exact age from input, fall back to Q1 age range
      const exactAge = this.state.answers.exact_age;
      const ageRangeAnswer = this.state.answers.age;

      let baseAge;
      if (exactAge && exactAge.value) {
        baseAge = parseInt(exactAge.value, 10);
      } else if (ageRangeAnswer) {
        baseAge = AGE_MAP[ageRangeAnswer.value] || 45;
      } else {
        return null;
      }

      // Perceived age = actual age + 10 years (low T makes them look/feel older)
      return baseAge + 10;
    }

    getGoalAge() {
      // Prefer exact age from input, fall back to Q1 age range
      const exactAge = this.state.answers.exact_age;
      const ageRangeAnswer = this.state.answers.age;

      let baseAge;
      if (exactAge && exactAge.value) {
        baseAge = parseInt(exactAge.value, 10);
      } else if (ageRangeAnswer) {
        baseAge = AGE_MAP[ageRangeAnswer.value] || 45;
      } else {
        return null;
      }

      // Goal age = actual age - 8 years (optimized T makes them feel younger)
      return baseAge - 8;
    }

    getProblemAreasText(limit = 2) {
      const problemAreas = this.state.answers.problem_areas;
      if (!problemAreas || !Array.isArray(problemAreas)) return 'your concerns';
      
      const labels = problemAreas.slice(0, limit).map(p => PROBLEM_LABELS[p.value] || p.label);
      return labels.join(' & ');
    }

    getDatePlus(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    populateSummaryProfile(step) {
      const scores = this.calculateTScores();
      this.populateProblemAreasVisual(step);
      this.populateBioAge(step, scores);
      this.populateClinicalSummary(step, scores);
      this.animateDashboard(step, scores);
    }

    /* ---- Scoring Engine ---- */

    calculateTScores() {
      const a = this.state.answers;
      const val = (key) => a[key]?.value || '';

      // Hormonal Health (0–100): morning_wood + afternoon_crash
      const mwMap = { every_morning: 95, most_mornings: 70, occasionally: 40, rarely: 15 };
      const acMap = { never: 95, sometimes: 65, often: 35, always: 10 };
      const mwScore = mwMap[val('morning_wood')] ?? 50;
      const acScore = acMap[val('afternoon_crash')] ?? 50;
      const hormonal = Math.round(mwScore * 0.55 + acScore * 0.45);

      // Lifestyle Impact (0–100): exercise + belly_fat + alcohol
      const exMap = { '5_plus_week': 95, '3_4_week': 72, '1_2_week': 45, rarely: 15 };
      const bfMap = { flat: 95, slight: 75, moderate: 50, significant: 25, severe: 8 };
      const alMap = { never: 95, rarely: 80, occasionally: 60, regularly: 35, daily: 10 };
      const exScore = exMap[val('exercise')] ?? 50;
      const bfScore = bfMap[val('belly_fat')] ?? 50;
      const alScore = alMap[val('alcohol')] ?? 50;
      const lifestyle = Math.round(exScore * 0.4 + bfScore * 0.35 + alScore * 0.25);

      // Body Composition (0–100): body_type vs dream_body gap
      const bodyOrder = ['overweight', 'soft', 'slim', 'skinny', 'muscular'];
      const dreamOrder = ['fit_healthy', 'lean_toned', 'slim_defined', 'athletic', 'muscular', 'bulk_power'];
      const btIdx = bodyOrder.indexOf(val('body_type'));
      const dbIdx = dreamOrder.indexOf(val('dream_body'));
      let bodyGap;
      if (btIdx === -1 || dbIdx === -1) {
        bodyGap = 3;
      } else {
        // Higher gap = worse score. Muscular current + any dream = small gap
        const currentRank = [15, 35, 60, 50, 90][btIdx]; // base fitness rank
        const dreamDifficulty = [60, 70, 65, 80, 90, 95][dbIdx];
        bodyGap = Math.max(0, dreamDifficulty - currentRank);
      }
      const body = Math.round(Math.max(5, Math.min(95, 95 - bodyGap)));

      // Biological Age (0–100): age + modifier from hormonal & lifestyle
      const ageMap = { under_30: 27, '30_39': 35, '40_49': 45, '50_59': 55, over_60: 63 };
      const baseAge = ageMap[val('age')] || 45;
      // Worse hormonal/lifestyle = higher biological age penalty
      const healthAvg = (hormonal + lifestyle) / 2;
      const agePenalty = Math.round((100 - healthAvg) * 0.18);
      const biologicalAge = baseAge + agePenalty;
      const bioageScore = Math.round(Math.max(5, Math.min(95, healthAvg * 0.6 + (100 - agePenalty * 2) * 0.4)));

      // Cap individual metrics — best case should still look concerning
      const MAX_METRIC = 58;
      const MIN_AGE_PENALTY = 4;
      const cappedHormonal = Math.min(hormonal, MAX_METRIC);
      const cappedLifestyle = Math.min(lifestyle, MAX_METRIC);
      const cappedBody = Math.min(body, MAX_METRIC);
      const cappedBioage = Math.min(bioageScore, MAX_METRIC);
      const cappedAgePenalty = Math.max(agePenalty, MIN_AGE_PENALTY);
      const cappedBiologicalAge = baseAge + cappedAgePenalty;

      // Overall T-Score: weighted average (also capped)
      const tscore = Math.min(MAX_METRIC, Math.round(
        cappedHormonal * 0.40 +
        cappedLifestyle * 0.30 +
        cappedBody * 0.20 +
        cappedBioage * 0.10
      ));

      return {
        tscore, hormonal: cappedHormonal, lifestyle: cappedLifestyle,
        body: cappedBody, bioage: cappedBioage,
        chronoAge: baseAge, biologicalAge: cappedBiologicalAge,
        agePenalty: cappedAgePenalty
      };
    }

    getSeverity(score) {
      if (score <= 25) return { level: 'critical', label: 'Critical', color: '#e43044' };
      if (score <= 45) return { level: 'low', label: 'Low', color: '#e67e22' };
      if (score <= 60) return { level: 'below_avg', label: 'Below Avg', color: '#e6a817' };
      if (score <= 78) return { level: 'average', label: 'Average', color: '#2980b9' };
      return { level: 'optimal', label: 'Optimal', color: '#05ac2e' };
    }

    getMetricDetail(key, score) {
      const a = this.state.answers;
      const sev = this.getSeverity(score);
      const details = {
        hormonal: {
          critical: 'Significant hormonal disruption detected based on your symptoms.',
          low: 'Your morning and energy patterns suggest declining hormone levels.',
          below_avg: 'Mild hormonal imbalance — room for improvement.',
          average: 'Hormonal markers are fair but not fully optimized.',
          optimal: 'Strong hormonal health indicators across the board.'
        },
        lifestyle: {
          critical: 'Your current habits are severely impacting testosterone production.',
          low: 'Lifestyle factors like exercise and alcohol are dragging T-levels down.',
          below_avg: 'Some lifestyle habits are holding back your testosterone.',
          average: 'Decent lifestyle habits — small adjustments will help.',
          optimal: 'Your lifestyle strongly supports natural T production.'
        },
        body: {
          critical: 'Large gap between current and ideal body composition.',
          low: 'Body composition indicates elevated estrogen conversion.',
          below_avg: 'Moderate body composition gap to close.',
          average: 'Body composition is reasonable with room to improve.',
          optimal: 'Near-optimal body composition for testosterone.'
        },
        bioage: {
          critical: 'Your body is aging significantly faster than your calendar age.',
          low: 'Biological markers suggest accelerated aging.',
          below_avg: 'Slight acceleration in biological aging detected.',
          average: 'Biological age is close to chronological age.',
          optimal: 'Your body is aging slower than average — great sign.'
        }
      };
      return (details[key] && details[key][sev.level]) || '';
    }

    getTScoreDescription(score) {
      if (score <= 25) return 'Your testosterone profile shows critical deficiency across multiple markers. Immediate action recommended.';
      if (score <= 45) return 'Your results indicate low testosterone with noticeable impact on energy, body composition, and vitality.';
      if (score <= 60) return 'Your testosterone is below average. Targeted support can help restore optimal levels.';
      if (score <= 78) return 'Your testosterone is in a fair range but has room for meaningful improvement.';
      return 'Your testosterone profile is strong. Maintain your current habits for peak performance.';
    }

    /* ---- Problem Areas Visual ---- */

    populateProblemAreasVisual(step) {
      const problemAreasVisual = step.querySelector('.croev-quiz__problem-areas-visual');
      const problemAreas = this.state.answers.problem_areas;

      if (problemAreasVisual && problemAreas && Array.isArray(problemAreas) && problemAreas.length > 0) {
        const selectedAreas = problemAreas.slice(0, 3);
        let html = '<div class="croev-quiz__problem-areas-grid">';

        const imageMap = {
          'beer_belly': problemAreasVisual.dataset.imgBeerbelly,
          'man_boobs': problemAreasVisual.dataset.imgManboobs,
          'low_t': problemAreasVisual.dataset.imgLowt,
          'high_cortisol': problemAreasVisual.dataset.imgHighcortisol,
          'low_energy': problemAreasVisual.dataset.imgLowenergy,
          'low_muscle': problemAreasVisual.dataset.imgLowmuscle
        };

        const labelMap = {
          'beer_belly': 'Beer Belly',
          'man_boobs': 'Man Boobs',
          'low_t': 'Low Testosterone',
          'high_cortisol': 'High Cortisol',
          'low_energy': 'Low Energy/Libido',
          'low_muscle': 'Low Muscle Mass'
        };

        selectedAreas.forEach(area => {
          const imageUrl = imageMap[area.value];
          const label = labelMap[area.value] || PROBLEM_LABELS[area.value] || area.label;
          if (imageUrl && imageUrl.length > 10) {
            html += `
              <div class="croev-quiz__problem-area-item">
                <img src="${imageUrl}" alt="${label}" class="croev-quiz__problem-area-img" loading="lazy">
                <span class="croev-quiz__problem-area-label">${label}</span>
              </div>
            `;
          }
        });

        html += '</div>';
        problemAreasVisual.innerHTML = html;
      }
    }

    /* ---- Biological Age ---- */

    populateBioAge(step, scores) {
      const chronoEl = step.querySelector('[data-field="chrono_age"]');
      const bioEl = step.querySelector('[data-field="bio_age"]');
      const gapEl = step.querySelector('[data-field="age_gap"]');

      if (chronoEl) chronoEl.textContent = scores.chronoAge;
      if (bioEl) bioEl.textContent = scores.biologicalAge;
      if (gapEl) gapEl.textContent = `+${scores.agePenalty} yrs`;
    }

    /* ---- Clinical Summary ---- */

    populateClinicalSummary(step, scores) {
      const el = step.querySelector('[data-field="clinical_summary"]');
      if (!el) return;
      const existing = el.textContent.trim();
      if (existing && existing !== '--') return; // Don't overwrite CMS content

      const problemAreasText = this.getProblemAreasText();
      const sev = this.getSeverity(scores.tscore);
      el.textContent = `Based on your answers, your T-Score of ${scores.tscore} (${sev.label}) suggests your ${problemAreasText} and lifestyle factors are impacting testosterone levels. Your body may be functioning like someone ${scores.biologicalAge} years old.`;
    }

    /* ---- Animate Dashboard ---- */

    animateDashboard(step, scores) {
      const GAUGE_CIRCUMFERENCE = 2 * Math.PI * 70; // ~439.82
      const ANIM_DURATION = 1800;
      const metrics = ['hormonal', 'lifestyle', 'body', 'bioage'];

      // Animate T-Score gauge
      const gaugeFill = step.querySelector('[data-gauge="tscore"]');
      const gaugeCounter = step.querySelector('[data-counter="tscore"]');
      const gaugeBadge = step.querySelector('[data-severity="tscore"]');
      const gaugeDesc = step.querySelector('[data-desc="tscore"]');

      if (gaugeFill) {
        const sev = this.getSeverity(scores.tscore);
        const target = GAUGE_CIRCUMFERENCE * (1 - scores.tscore / 100);
        // Small delay so CSS animation can kick in
        requestAnimationFrame(() => {
          gaugeFill.style.strokeDashoffset = target;
          gaugeFill.style.stroke = sev.color;
        });
        if (gaugeBadge) {
          gaugeBadge.textContent = sev.label;
          gaugeBadge.setAttribute('data-level', sev.level);
        }
        if (gaugeDesc) {
          gaugeDesc.textContent = this.getTScoreDescription(scores.tscore);
        }
      }

      // Animate counter (T-Score number)
      if (gaugeCounter) {
        this.animateCounter(gaugeCounter, scores.tscore, ANIM_DURATION);
      }

      // Animate each metric card
      metrics.forEach((key, i) => {
        const score = scores[key];
        const sev = this.getSeverity(score);
        const delay = 600 + i * 150;

        setTimeout(() => {
          const bar = step.querySelector(`[data-bar="${key}"]`);
          const counter = step.querySelector(`[data-counter="${key}"]`);
          const badge = step.querySelector(`[data-severity="${key}"]`);
          const detail = step.querySelector(`[data-detail="${key}"]`);

          if (bar) {
            bar.style.width = `${score}%`;
            bar.style.backgroundColor = sev.color;
          }
          if (counter) {
            this.animateCounter(counter, score, 1400);
          }
          if (badge) {
            badge.textContent = sev.label;
            badge.setAttribute('data-level', sev.level);
          }
          if (detail) {
            detail.textContent = this.getMetricDetail(key, score);
          }
        }, delay);
      });
    }

    animateCounter(el, target, duration) {
      const start = performance.now();
      const from = 0;
      const tick = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        // Ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.round(from + (target - from) * eased);
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    populateSummaryResults(step) {
      const answers = this.state.answers;
      const val = (key) => answers[key]?.value || '';
      const label = (key) => answers[key]?.label || '';
      const date30 = this.getDatePlus(30);
      const date60 = this.getDatePlus(60);

      // --- Personalized headline ---
      const personalizedEl = step.querySelector('[data-sr-personalized]');
      if (personalizedEl) {
        const concerns = this.getProblemAreasTextBold(3);
        const age = label('age');
        const motivation = val('motivation');
        const motivationMap = {
          'extremely_motivated': "Your determination puts you ahead of 90% of men",
          'very_motivated': "Your commitment level is exactly what's needed",
          'motivated': "With the right support, you're set for real change",
          'somewhat': "Even small steps lead to big transformations"
        };
        const motivationLine = motivationMap[motivation] || "You're taking the first step toward real change";
        personalizedEl.innerHTML = `${motivationLine}. Based on your profile${age ? ' as a <strong>' + age + '</strong> male' : ''} dealing with ${concerns}, here's your personalized plan:`;
      }

      // --- Profile pills ---
      const profileGrid = step.querySelector('[data-sr-profile]');
      if (profileGrid) {
        const pills = [];
        const addPill = (lbl, value) => {
          if (value) pills.push(`<span class="croev-quiz__sr-profile-pill"><span class="croev-quiz__sr-profile-pill-label">${lbl}:</span> <span class="croev-quiz__sr-profile-pill-value">${value}</span></span>`);
        };
        addPill('Age', label('age'));
        addPill('Body Type', label('body_type'));
        addPill('Goal', label('dream_body'));
        addPill('Exercise', label('exercise'));
        addPill('Belly Fat', label('belly_fat'));
        if (val('special_event') && val('special_event') !== 'no_event' && val('special_event') !== 'self_improvement') {
          addPill('Event', label('special_event'));
        }
        profileGrid.innerHTML = pills.join('');
      }

      // --- Event countdown ---
      const eventDate = val('event_date');
      const countdownEl = step.querySelector('[data-sr-countdown]');
      if (eventDate && countdownEl) {
        const eventDateObj = new Date(eventDate + 'T00:00:00');
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((eventDateObj - now) / (1000 * 60 * 60 * 24));
        if (diffDays > 0) {
          const eventType = val('special_event');
          const eventLabels = {
            'vacation': 'Your trip',
            'wedding': 'Your event',
            'fitness': 'Your competition',
            'health_checkup': 'Your checkup'
          };
          const eventLabel = eventLabels[eventType] || 'Your target date';
          const formattedDate = eventDateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
          step.querySelector('[data-sr-countdown-label]').textContent = `${eventLabel}: ${formattedDate}`;
          step.querySelector('[data-sr-countdown-days]').textContent = `${diffDays} days away`;
          countdownEl.hidden = false;
        }
      }

      // --- Timeline dates ---
      const d30El = step.querySelector('[data-sr-date-30]');
      const d60El = step.querySelector('[data-sr-date-60]');
      if (d30El) d30El.textContent = date30;
      if (d60El) d60El.textContent = date60;

      // Replace {date_30} / {date_60} in template paragraphs
      step.querySelectorAll('[data-template]').forEach(el => {
        const template = el.dataset.template;
        el.innerHTML = template
          .replace('{date_30}', date30)
          .replace('{date_60}', date60);
      });

      // --- Personalized benefits based on concerns ---
      const benefitsEl = step.querySelector('[data-sr-benefits]');
      if (benefitsEl) {
        const problemAreas = answers.problem_areas;
        const benefitMap = {
          'beer_belly': 'Target <strong>stubborn belly fat</strong> with cortisol-lowering adaptogens',
          'man_boobs': 'Support <strong>estrogen balance</strong> to reduce chest fat',
          'low_t': 'Naturally boost <strong>testosterone production</strong> with reindeer velvet antler',
          'high_cortisol': 'Lower <strong>cortisol levels</strong> with proven stress-fighting herbs',
          'low_energy': 'Restore <strong>all-day energy & drive</strong> starting in the first week',
          'low_muscle': 'Accelerate <strong>lean muscle growth</strong> and workout recovery'
        };
        const checkSvg = '<svg class="croev-quiz__sr-benefit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>';
        let html = '';
        if (Array.isArray(problemAreas)) {
          problemAreas.forEach(a => {
            const text = benefitMap[a.value];
            if (text) html += `<div class="croev-quiz__sr-benefit">${checkSvg}<p class="croev-quiz__sr-benefit-text">${text}</p></div>`;
          });
        }
        if (!html) {
          // Fallback benefits
          html = `<div class="croev-quiz__sr-benefit">${checkSvg}<p class="croev-quiz__sr-benefit-text">Naturally boost <strong>testosterone production</strong> with reindeer velvet antler</p></div>`;
          html += `<div class="croev-quiz__sr-benefit">${checkSvg}<p class="croev-quiz__sr-benefit-text">Restore <strong>all-day energy & drive</strong> starting in the first week</p></div>`;
        }
        benefitsEl.innerHTML = html;
      }
    }

    populateSummaryComparison(step) {
      const perceivedAge = this.getPerceivedAge();
      const goalAge = this.getGoalAge();
      const ageAnswer = this.state.answers.age?.value;

      // Update ages
      const perceivedAgeEl = step.querySelector('[data-field="perceived_age"]');
      if (perceivedAgeEl && perceivedAge) {
        perceivedAgeEl.textContent = `${perceivedAge} years`;
      }

      const goalAgeEl = step.querySelector('[data-field="goal_age"]');
      if (goalAgeEl && goalAge) {
        goalAgeEl.textContent = `${goalAge} years`;
      }

      // Update years younger text
      const yearsYoungerEl = step.querySelector('[data-field="years_younger"]');
      if (yearsYoungerEl && perceivedAge && goalAge) {
        const yearsDiff = perceivedAge - goalAge;
        yearsYoungerEl.textContent = `${yearsDiff}+ years`;
      }

      // Update images using age images from quiz config
      const ageImages = this.config.ageImages || {};
      const nowImageUrl = ageAnswer ? ageImages[ageAnswer] : null;

      if (nowImageUrl && nowImageUrl.length > 0) {
        // NOW image = user's selected age
        const nowImg = step.querySelector('[data-image-type="now"]');
        if (nowImg) {
          nowImg.style.backgroundImage = `url('${nowImageUrl}')`;
          // Remove placeholder
          const placeholder = nowImg.querySelector('.croev-quiz__comparison-card-placeholder');
          if (placeholder) placeholder.style.display = 'none';
        }

        // GOAL image = younger age bracket (2 steps younger, min is under_35)
        // If already at youngest tier (under_35), use special "youngest_goal" image
        const currentAgeIndex = AGE_ORDER.indexOf(ageAnswer);
        const goalAgeIndex = Math.max(0, currentAgeIndex - 2);
        let goalImageUrl;

        if (currentAgeIndex === 0) {
          // User selected youngest tier - use the special youngest goal image
          goalImageUrl = ageImages['youngest_goal'];
        } else {
          const goalAgeKey = AGE_ORDER[goalAgeIndex];
          goalImageUrl = ageImages[goalAgeKey];
        }

        const goalImg = step.querySelector('[data-image-type="goal"]');
        if (goalImg && goalImageUrl && goalImageUrl.length > 0) {
          goalImg.style.backgroundImage = `url('${goalImageUrl}')`;
          // Remove placeholder
          const placeholder = goalImg.querySelector('.croev-quiz__comparison-card-placeholder');
          if (placeholder) placeholder.style.display = 'none';
        }
      }
    }

    populateInfoStep(step) {
      // Handle dynamic subheadline with {problem_areas} placeholder
      const subheadlineEl = step.querySelector('.croev-quiz__subheadline--dynamic');
      if (subheadlineEl) {
        const template = subheadlineEl.dataset.subheadlineTemplate || subheadlineEl.textContent;
        const problemAreasText = this.getProblemAreasTextBold();
        const content = template.replace('{problem_areas}', problemAreasText);
        subheadlineEl.innerHTML = content;
      }

      // Testimonial carousel arrow navigation
      const testimonialCarousel = step.querySelector('[data-testimonial-carousel]');
      if (testimonialCarousel && !testimonialCarousel.dataset.initialized) {
        const track = testimonialCarousel.querySelector('[data-testimonial-track]');
        const prevBtn = testimonialCarousel.querySelector('[data-testimonial-prev]');
        const nextBtn = testimonialCarousel.querySelector('[data-testimonial-next]');
        if (track && prevBtn && nextBtn) {
          const scrollAmount = () => {
            const card = track.querySelector('.croev-quiz__testimonial-card');
            return card ? card.offsetWidth + 16 : 296;
          };
          prevBtn.addEventListener('click', () => {
            track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
          });
          nextBtn.addEventListener('click', () => {
            track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
          });
        }
        testimonialCarousel.dataset.initialized = 'true';
      }
    }

    populateOfferStep(step) {
      const answers = this.state.answers;

      // Personalized subheadline
      const personalizedEl = step.querySelector('[data-offer-personalized]');
      if (personalizedEl) {
        const concerns = this.getProblemAreasTextBold(3);
        const severity = answers.severity;
        const severityLabel = severity ? severity.label : '';

        let text = `Based on your <strong>${severityLabel || 'testosterone'}</strong> concerns with ${concerns}, we recommend Primal Reindeer Testosterone Support.`;
        personalizedEl.innerHTML = text;
      }

      // Personalized benefits
      const benefitsEl = step.querySelector('[data-offer-benefits]');
      if (benefitsEl) {
        const checkSvg = '<svg class="croev-quiz__offer-benefit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';

        // Map concerns to specific benefits
        const benefitMap = {
          'beer_belly': 'Targets <strong>stubborn belly fat</strong> by naturally optimizing testosterone levels',
          'man_boobs': 'Helps reduce <strong>chest fat</strong> by rebalancing testosterone and estrogen',
          'low_t': 'Clinically-studied ingredients to <strong>boost natural testosterone</strong> production',
          'high_cortisol': 'Adaptogenic herbs that <strong>lower cortisol</strong> and reduce stress-related fat storage',
          'low_energy': 'Restores <strong>energy, drive & libido</strong> so you feel like yourself again',
          'low_muscle': 'Supports <strong>lean muscle growth</strong> and faster recovery from workouts'
        };

        // Default benefits that always show
        const defaultBenefits = [
          '100% natural <strong>reindeer velvet antler + Nordic botanicals</strong> — no synthetic hormones',
          'Formulated to support <strong>testosterone, energy & body composition</strong>',
          '<strong>30-day money-back guarantee</strong> — zero risk to try'
        ];

        let benefits = [];

        // Add concern-specific benefits
        const problemAreas = answers.problem_areas;
        if (problemAreas && Array.isArray(problemAreas)) {
          problemAreas.slice(0, 3).forEach(p => {
            const benefit = benefitMap[p.value];
            if (benefit) benefits.push(benefit);
          });
        }

        // Add time-based benefit
        const supplementTime = answers.supplement_time;
        if (supplementTime && supplementTime.value === 'morning') {
          benefits.push('Just <strong>2 capsules with breakfast</strong> — fits your morning routine');
        } else if (supplementTime) {
          benefits.push('Simple daily routine — <strong>just 2 capsules a day</strong>');
        }

        // Add motivation benefit
        const motivation = answers.motivation;
        if (motivation && motivation.value === 'very_motivated') {
          benefits.push('Your <strong>high motivation</strong> combined with our formula = fastest results');
        }

        // Fill with defaults if we don't have enough
        if (benefits.length < 3) {
          defaultBenefits.forEach(b => {
            if (benefits.length < 5 && !benefits.includes(b)) benefits.push(b);
          });
        }

        // Cap at 5
        benefits = benefits.slice(0, 5);

        let html = '<p class="croev-quiz__offer-benefits-title">Why this is perfect for you</p>';
        benefits.forEach(b => {
          html += `<div class="croev-quiz__offer-benefit">${checkSvg}<p class="croev-quiz__offer-benefit-text">${b}</p></div>`;
        });

        benefitsEl.innerHTML = html;
      }
    }

    // --- Offer Carousel ---

    initOfferCarousel(step) {
      const carousel = step.querySelector('[data-offer-carousel]');
      if (!carousel || carousel.dataset.carouselInit) return;
      carousel.dataset.carouselInit = 'true';

      const slides = carousel.querySelector('[data-offer-slides]');
      const slideEls = Array.from(slides.children);
      const thumbs = step.querySelectorAll('[data-thumb-index]');
      const prevBtn = carousel.querySelector('[data-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-carousel-next]');
      let current = 0;
      const total = slideEls.length;

      const goTo = (index) => {
        current = Math.max(0, Math.min(index, total - 1));
        slides.style.transform = `translateX(-${current * 100}%)`;
        slideEls.forEach((s, i) => s.classList.toggle('is-active', i === current));
        thumbs.forEach((t, i) => t.classList.toggle('is-active', i === current));
      };

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));
      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => goTo(parseInt(thumb.dataset.thumbIndex, 10)));
      });

      // Swipe support
      let startX = 0;
      let diffX = 0;
      carousel.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; diffX = 0; }, { passive: true });
      carousel.addEventListener('touchmove', (e) => { diffX = e.touches[0].clientX - startX; }, { passive: true });
      carousel.addEventListener('touchend', () => {
        if (Math.abs(diffX) > 40) {
          goTo(diffX > 0 ? current - 1 : current + 1);
        }
      });
    }

    getProblemAreasTextBold(limit = 3) {
      const problemAreas = this.state.answers.problem_areas;
      if (!problemAreas || !Array.isArray(problemAreas)) return '<strong>your concerns</strong>';

      const labels = problemAreas.slice(0, limit).map(p => PROBLEM_LABELS[p.value] || p.label);
      return '<strong>' + labels.join(' & ') + '</strong>';
    }

    handleLoadingStep(step) {
      const duration = parseInt(step.dataset.duration, 10) || 3000;
      const checklist = step.querySelector('.croev-quiz__checklist');

      if (checklist) {
        // Animate checklist items
        const items = Array.from(checklist.querySelectorAll('.croev-quiz__checklist-item'));
        items.forEach((item, i) => {
          const delay = parseInt(item.dataset.delay, 10) || i * 1000;
          
          const timer = setTimeout(() => {
            item.classList.add('is-visible');
            
            // Mark as complete after appearing
            setTimeout(() => {
              item.classList.add('is-complete');
            }, 500);
          }, delay);
          
          this.checklistTimers.push(timer);
        });
      }

      // Auto-advance after duration
      this.autoAdvanceTimer = setTimeout(() => {
        this.nextStep();
      }, duration);
    }

    clearTimers() {
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;
      }
      this.checklistTimers.forEach(t => clearTimeout(t));
      this.checklistTimers = [];
    }

    // --- Offer Handling ---

    handleSkip(btn) {
      // Skip this step without saving an answer
      const step = btn.closest('.croev-quiz__step');
      const questionId = step?.dataset.questionId;

      // Clear any answer for this question
      if (questionId && this.state.answers[questionId]) {
        delete this.state.answers[questionId];
        this.saveState();
      }

      // Move to next step
      this.nextStep();
    }

    handleClaimOffer(btn) {
      const productUrl = btn.dataset.productUrl;
      const variantId = btn.dataset.variantId;
      const sellingPlanId = btn.dataset.sellingPlanId;
      const step = btn.closest('.croev-quiz__step');
      const discountCode = step?.dataset.discountCode;

      // Build URL with quiz answers as parameters
      const params = new URLSearchParams();

      // Add all answers
      Object.entries(this.state.answers).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params.set(key, value.map(v => v.value).join(','));
        } else if (value && value.value) {
          params.set(key, value.value);
        }
      });

      // Add variant if available
      if (variantId) {
        params.set('variant', variantId);
      }

      // Add selling plan if available
      if (sellingPlanId) {
        params.set('selling_plan', sellingPlanId);
      }

      // Add discount code if available
      if (discountCode) {
        params.set('discount', discountCode);
      }

      // Redirect
      let url = this.redirectUrl || productUrl;
      if (url) {
        const separator = url.includes('?') ? '&' : '?';
        window.location.href = `${url}${separator}${params.toString()}`;
      }
    }

    // --- State Persistence ---

    saveState() {
      try {
        localStorage.setItem(`croev-quiz-${this.sectionId}`, JSON.stringify(this.state));
      } catch (e) {
        console.warn('Could not save quiz state:', e);
      }
    }

    loadState() {
      try {
        const saved = localStorage.getItem(`croev-quiz-${this.sectionId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Only restore answers, start from beginning
          this.state.answers = parsed.answers || {};
        }
      } catch (e) {
        console.warn('Could not load quiz state:', e);
      }
    }

    clearState() {
      try {
        localStorage.removeItem(`croev-quiz-${this.sectionId}`);
      } catch (e) {
        console.warn('Could not clear quiz state:', e);
      }
    }

    // --- Utilities ---

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    destroy() {
      this.clearTimers();
    }
  }

  // Initialize all quiz instances
  function initQuizzes() {
    document.querySelectorAll('[data-section-type="croev-quiz"]').forEach(container => {
      if (!container.croevQuiz) {
        container.croevQuiz = new CroevQuiz(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuizzes);
  } else {
    initQuizzes();
  }

  // Handle Shopify theme editor events
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container) {
      container.croevQuiz = new CroevQuiz(container);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container && container.croevQuiz) {
      container.croevQuiz.destroy();
    }
  });

})();
